import React, { useState } from 'react';

const BASE_URL = 'https://api.sroik.com';

const LANGS = ['Bash', 'Python3', 'Go', 'PHP', 'Ruby'];

const LANG_ICON = {
  Bash: '>_',
  Python3: 'PY3',
  Go: '🔵',
  PHP: '🐘',
  Ruby: '💎',
};

/* ── colour per language tag ── */
const LANG_COLOR = {
  Bash: '#d3f36b',
  Python3: '#4cc1e4',
  Go: '#00add8',
  PHP: '#8892be',
  Ruby: '#cc342d',
};

/* ── all code samples ── */
const SAMPLES = {
  authenticate: {
    Bash: `curl -sS "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json"`,

    Python3: `import requests

token = "sroik_pat_YOUR_TOKEN"
headers = {
    "Authorization": f"Bearer {token}",
    "Content-Type": "application/json",
}

resp = requests.get("${BASE_URL}/api/v1/cloud-accounts", headers=headers)
print(resp.json())`,

    Go: `package main

import (
\t"fmt"
\t"net/http"
\t"io"
)

func main() {
\treq, _ := http.NewRequest("GET", "${BASE_URL}/api/v1/cloud-accounts", nil)
\treq.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
\treq.Header.Set("Content-Type", "application/json")

\tclient := &http.Client{}
\tresp, err := client.Do(req)
\tif err != nil { panic(err) }
\tdefer resp.Body.Close()

\tbody, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(body))
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        "Authorization: Bearer sroik_pat_YOUR_TOKEN",
        "Content-Type: application/json",
    ],
]);
$response = json_decode(curl_exec($ch), true);
curl_close($ch);
print_r($response);`,

    Ruby: `require "net/http"
require "json"
require "uri"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Get.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"] = "application/json"

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) do |http|
  http.request(req)
end
puts JSON.parse(res.body)`,

  },

  create_token: {
    Bash: `curl -sS -X POST "${BASE_URL}/api/v1/auth/api-tokens" \
  -H "Authorization: Bearer SESSION_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"name":"ci-pipeline","expires_in_days":90}'`,

    Python3: `import requests

# First authenticate with email/password to get a session token
login = requests.post("${BASE_URL}/api/v1/auth/login", json={
    "email": "you@example.com",
    "password": "your_password",
})
session_token = login.json()["access_token"]

# Then create a personal API token
resp = requests.post(
    "${BASE_URL}/api/v1/auth/api-tokens",
    json={"name": "ci-pipeline", "expires_in_days": 90},
    headers={"Authorization": f"Bearer {session_token}"},
)
token = resp.json()["token"]  # save this — shown only once
print(token)`,

    Go: `package main

import (
\t"bytes"
\t"encoding/json"
\t"fmt"
\t"net/http"
\t"io"
)

func main() {
\tbody, _ := json.Marshal(map[string]any{
\t\t"name": "ci-pipeline", "expires_in_days": 90,
\t})
\treq, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/auth/api-tokens", bytes.NewReader(body))
\treq.Header.Set("Authorization", "Bearer SESSION_TOKEN")
\treq.Header.Set("Content-Type", "application/json")

\tclient := &http.Client{}
\tresp, _ := client.Do(req)
\tdefer resp.Body.Close()
\tout, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(out)) // {"token": "sroik_pat_..."}
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/auth/api-tokens");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode([
        "name" => "ci-pipeline",
        "expires_in_days" => 90,
    ]),
    CURLOPT_HTTPHEADER => [
        "Authorization: Bearer SESSION_TOKEN",
        "Content-Type: application/json",
    ],
]);
$result = json_decode(curl_exec($ch), true);
echo $result["token"]; // sroik_pat_...
curl_close($ch);`,

    Ruby: `require "net/http"
require "json"
require "uri"

uri = URI("${BASE_URL}/api/v1/auth/api-tokens")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer SESSION_TOKEN"
req["Content-Type"] = "application/json"
req.body = { name: "ci-pipeline", expires_in_days: 90 }.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts JSON.parse(res.body)["token"]`,

  },

  list_accounts: {
    Bash: `curl -sS "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN"`,

    Python3: `import requests

accounts = requests.get(
    "${BASE_URL}/api/v1/cloud-accounts",
    headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
).json()

for acct in accounts:
    print(acct["provider"], acct["account_id"], acct["status"])`,

    Go: `package main

import (
\t"encoding/json"
\t"fmt"
\t"net/http"
\t"io"
)

type Account struct {
\tProvider  string \`json:"provider"\`
\tAccountID string \`json:"account_id"\`
\tStatus    string \`json:"status"\`
}

func main() {
\treq, _ := http.NewRequest("GET", "${BASE_URL}/api/v1/cloud-accounts", nil)
\treq.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")

\tresp, _ := http.DefaultClient.Do(req)
\tdefer resp.Body.Close()
\tbody, _ := io.ReadAll(resp.Body)

\tvar accounts []Account
\tjson.Unmarshal(body, &accounts)
\tfor _, a := range accounts {
\t\tfmt.Println(a.Provider, a.AccountID, a.Status)
\t}
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["Authorization: Bearer sroik_pat_YOUR_TOKEN"],
]);
$accounts = json_decode(curl_exec($ch), true);
foreach ($accounts as $acct) {
    echo "{$acct['provider']} {$acct['account_id']} {$acct['status']}\\n";
}`,

    Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Get.new(uri, "Authorization" => "Bearer sroik_pat_YOUR_TOKEN")
res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }

JSON.parse(res.body).each do |acct|
  puts "#{acct['provider']} #{acct['account_id']} #{acct['status']}"
end`,

  },

  add_aws: {
    Bash: `curl -sS -X POST "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "provider":"aws",
    "account_id":"123456789012",
    "role_arn":"arn:aws:iam::123456789012:role/SroikReadOnly",
    "external_id":"sroik-aws-YOUR_EXTERNAL_ID"
  }'`,

    Python3: `import requests

resp = requests.post(
    "${BASE_URL}/api/v1/cloud-accounts",
    headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
    json={
        "provider": "aws",
        "account_id": "123456789012",
        "role_arn": "arn:aws:iam::123456789012:role/SroikReadOnly",
        "external_id": "sroik-aws-YOUR_EXTERNAL_ID",
    },
)
print(resp.status_code, resp.json())`,

    Go: `package main

import (
\t"bytes"
\t"encoding/json"
\t"fmt"
\t"net/http"
\t"io"
)

func main() {
\tbody, _ := json.Marshal(map[string]string{
\t\t"provider":    "aws",
\t\t"account_id":  "123456789012",
\t\t"role_arn":    "arn:aws:iam::123456789012:role/SroikReadOnly",
\t\t"external_id": "sroik-aws-YOUR_EXTERNAL_ID",
\t})
\treq, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/cloud-accounts", bytes.NewReader(body))
\treq.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
\treq.Header.Set("Content-Type", "application/json")
\tresp, _ := http.DefaultClient.Do(req)
\tdefer resp.Body.Close()
\tout, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(out))
}`,

    PHP: `<?php
$payload = json_encode([
    "provider"    => "aws",
    "account_id"  => "123456789012",
    "role_arn"    => "arn:aws:iam::123456789012:role/SroikReadOnly",
    "external_id" => "sroik-aws-YOUR_EXTERNAL_ID",
]);
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => $payload,
    CURLOPT_HTTPHEADER => [
        "Authorization: Bearer sroik_pat_YOUR_TOKEN",
        "Content-Type: application/json",
    ],
]);
print_r(json_decode(curl_exec($ch), true));`,

    Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"]  = "application/json"
req.body = {
  provider:    "aws",
  account_id:  "123456789012",
  role_arn:    "arn:aws:iam::123456789012:role/SroikReadOnly",
  external_id: "sroik-aws-YOUR_EXTERNAL_ID",
}.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,

  },

  add_gcp: {
    Bash: `curl -sS -X POST "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"provider":"gcp","account_id":"my-gcp-project-id"}'`,

    Python3: `import requests

resp = requests.post(
    "${BASE_URL}/api/v1/cloud-accounts",
    headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
    json={
        "provider": "gcp",
        "account_id": "my-gcp-project-id",
    },
)
print(resp.status_code, resp.json())`,

    Go: `package main

import (
\t"bytes"
\t"encoding/json"
\t"fmt"
\t"net/http"
\t"io"
)

func main() {
\tbody, _ := json.Marshal(map[string]string{
\t\t"provider":   "gcp",
\t\t"account_id": "my-gcp-project-id",
\t})
\treq, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/cloud-accounts", bytes.NewReader(body))
\treq.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
\treq.Header.Set("Content-Type", "application/json")
\tresp, _ := http.DefaultClient.Do(req)
\tdefer resp.Body.Close()
\tout, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(out))
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode([
        "provider"   => "gcp",
        "account_id" => "my-gcp-project-id",
    ]),
    CURLOPT_HTTPHEADER => [
        "Authorization: Bearer sroik_pat_YOUR_TOKEN",
        "Content-Type: application/json",
    ],
]);
print_r(json_decode(curl_exec($ch), true));`,

    Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"]  = "application/json"
req.body = { provider: "gcp", account_id: "my-gcp-project-id" }.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,

  },

  add_oci: {
  Bash: `curl -sS -X POST "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"provider":"oracle","account_id":"ocid1.tenancy.oc1..exampleuniqueID"}'`,

  Python3: `import requests

resp = requests.post(
  "${BASE_URL}/api/v1/cloud-accounts",
  headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
  json={
    "provider": "oracle",
    "account_id": "ocid1.tenancy.oc1..exampleuniqueID",
  },
)
print(resp.status_code, resp.json())`,

  Go: `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body, _ := json.Marshal(map[string]string{
		"provider":   "oracle",
		"account_id": "ocid1.tenancy.oc1..exampleuniqueID",
	})
	req, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/cloud-accounts", bytes.NewReader(body))
	req.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
	req.Header.Set("Content-Type", "application/json")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,

  PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => json_encode([
    "provider" => "oracle",
    "account_id" => "ocid1.tenancy.oc1..exampleuniqueID",
  ]),
  CURLOPT_HTTPHEADER => [
    "Authorization: Bearer sroik_pat_YOUR_TOKEN",
    "Content-Type: application/json",
  ],
]);
print_r(json_decode(curl_exec($ch), true));`,

  Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"]  = "application/json"
req.body = { provider: "oracle", account_id: "ocid1.tenancy.oc1..exampleuniqueID" }.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,
  },

  add_azure: {
  Bash: `curl -sS -X POST "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"provider":"azure","account_id":"00000000-0000-0000-0000-000000000000"}'`,

  Python3: `import requests

resp = requests.post(
  "${BASE_URL}/api/v1/cloud-accounts",
  headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
  json={
    "provider": "azure",
    "account_id": "00000000-0000-0000-0000-000000000000",
  },
)
print(resp.status_code, resp.json())`,

  Go: `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body, _ := json.Marshal(map[string]string{
		"provider":   "azure",
		"account_id": "00000000-0000-0000-0000-000000000000",
	})
	req, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/cloud-accounts", bytes.NewReader(body))
	req.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
	req.Header.Set("Content-Type", "application/json")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,

  PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => json_encode([
    "provider" => "azure",
    "account_id" => "00000000-0000-0000-0000-000000000000",
  ]),
  CURLOPT_HTTPHEADER => [
    "Authorization: Bearer sroik_pat_YOUR_TOKEN",
    "Content-Type: application/json",
  ],
]);
print_r(json_decode(curl_exec($ch), true));`,

  Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"]  = "application/json"
req.body = { provider: "azure", account_id: "00000000-0000-0000-0000-000000000000" }.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,
  },

  add_ibmcloud: {
  Bash: `curl -sS -X POST "${BASE_URL}/api/v1/cloud-accounts" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{"provider":"ibmcloud","account_id":"crn:v1:bluemix:public:resource-controller::a/1234567890abcdef::"}'`,

  Python3: `import requests

resp = requests.post(
  "${BASE_URL}/api/v1/cloud-accounts",
  headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
  json={
    "provider": "ibmcloud",
    "account_id": "crn:v1:bluemix:public:resource-controller::a/1234567890abcdef::",
  },
)
print(resp.status_code, resp.json())`,

  Go: `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"io"
	"net/http"
)

func main() {
	body, _ := json.Marshal(map[string]string{
		"provider":   "ibmcloud",
		"account_id": "crn:v1:bluemix:public:resource-controller::a/1234567890abcdef::",
	})
	req, _ := http.NewRequest("POST", "${BASE_URL}/api/v1/cloud-accounts", bytes.NewReader(body))
	req.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
	req.Header.Set("Content-Type", "application/json")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	out, _ := io.ReadAll(resp.Body)
	fmt.Println(string(out))
}`,

  PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts");
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => json_encode([
    "provider" => "ibmcloud",
    "account_id" => "crn:v1:bluemix:public:resource-controller::a/1234567890abcdef::",
  ]),
  CURLOPT_HTTPHEADER => [
    "Authorization: Bearer sroik_pat_YOUR_TOKEN",
    "Content-Type: application/json",
  ],
]);
print_r(json_decode(curl_exec($ch), true));`,

  Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/cloud-accounts")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer sroik_pat_YOUR_TOKEN"
req["Content-Type"]  = "application/json"
req.body = {
  provider: "ibmcloud",
  account_id: "crn:v1:bluemix:public:resource-controller::a/1234567890abcdef::",
}.to_json

res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,
  },

  recommendations: {
    Bash: `curl -sS "${BASE_URL}/api/v1/recommendations" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN"`,

    Python3: `import requests

recs = requests.get(
    "${BASE_URL}/api/v1/recommendations",
    headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
).json()

for r in recs:
    print(r["severity"], r["title"], "·", r["account_id"])`,

    Go: `package main

import (
\t"fmt"
\t"net/http"
\t"io"
)

func main() {
\treq, _ := http.NewRequest("GET", "${BASE_URL}/api/v1/recommendations", nil)
\treq.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
\tresp, _ := http.DefaultClient.Do(req)
\tdefer resp.Body.Close()
\tbody, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(body))
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/recommendations");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["Authorization: Bearer sroik_pat_YOUR_TOKEN"],
]);
$recs = json_decode(curl_exec($ch), true);
foreach ($recs as $r) {
    echo "{$r['severity']}: {$r['title']}\\n";
}`,

    Ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/api/v1/recommendations")
req = Net::HTTP::Get.new(uri, "Authorization" => "Bearer sroik_pat_YOUR_TOKEN")
res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }

JSON.parse(res.body).each do |r|
  puts "#{r['severity']}: #{r['title']}"
end`,

  },

  sync_job_status: {
    Bash: `curl -sS "${BASE_URL}/api/v1/cloud-accounts/sync-jobs/JOB_ID" \
  -H "Authorization: Bearer sroik_pat_YOUR_TOKEN"`,

    Python3: `import requests

job = requests.get(
    "${BASE_URL}/api/v1/cloud-accounts/sync-jobs/JOB_ID",
    headers={"Authorization": "Bearer sroik_pat_YOUR_TOKEN"},
).json()

print(job["status"], job.get("result", {}))`,

    Go: `package main

import (
	"fmt"
	"io"
	"net/http"
)

func main() {
	req, _ := http.NewRequest("GET", "${BASE_URL}/api/v1/cloud-accounts/sync-jobs/JOB_ID", nil)
	req.Header.Set("Authorization", "Bearer sroik_pat_YOUR_TOKEN")
	resp, _ := http.DefaultClient.Do(req)
	defer resp.Body.Close()
	body, _ := io.ReadAll(resp.Body)
	fmt.Println(string(body))
}`,

    PHP: `<?php
$ch = curl_init("${BASE_URL}/api/v1/cloud-accounts/sync-jobs/JOB_ID");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["Authorization: Bearer sroik_pat_YOUR_TOKEN"],
]);
echo curl_exec($ch);`,

    Ruby: `require "net/http"

uri = URI("${BASE_URL}/api/v1/cloud-accounts/sync-jobs/JOB_ID")
req = Net::HTTP::Get.new(uri, "Authorization" => "Bearer sroik_pat_YOUR_TOKEN")
res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }
puts res.body`,
  },
};

/* ── section definitions ── */
const SECTIONS = [
  {
    id: 'authentication',
    title: 'Authentication',
    badge: 'Auth',
    badgeColor: '#4cc1e4',
    description: 'All API requests require a Bearer token. Create a personal API token from the Security page and pass it in the Authorization header.',
    headers: [
      { name: 'Authorization', required: true, desc: 'Bearer sroik_pat_...' },
      { name: 'Content-Type', required: false, desc: 'application/json (for POST/PUT)' },
    ],
    endpoints: [],
    sampleKey: 'authenticate',
    sampleTitle: 'Authenticated request',
  },
  {
    id: 'tokens',
    title: 'API Tokens',
    badge: 'POST',
    badgeColor: '#2ec6b1',
    description: 'Programmatically create long-lived personal API tokens for CI/CD pipelines and automation.',
    endpoints: [
      { method: 'GET', path: '/api/v1/auth/api-tokens', desc: 'List all your tokens' },
      { method: 'POST', path: '/api/v1/auth/api-tokens', desc: 'Create a new token' },
      { method: 'POST', path: '/api/v1/auth/api-tokens/{id}/revoke', desc: 'Revoke a token' },
      { method: 'DELETE', path: '/api/v1/auth/api-tokens/{id}', desc: 'Delete a token' },
    ],
    sampleKey: 'create_token',
    sampleTitle: 'Create an API token',
  },
  {
    id: 'cloud-accounts',
    title: 'Cloud Accounts',
    badge: 'GET',
    badgeColor: '#4cc1e4',
    description: 'Connect and manage AWS, GCP, Oracle/OCI, Azure, and IBM Cloud accounts. Sroik uses read-only access to scan resources and generate cost and security recommendations.',
    endpoints: [
      { method: 'GET', path: '/api/v1/cloud-accounts/bootstrap?provider={provider}&account_id={optional}', desc: 'Get provider setup guidance and onboarding metadata' },
      { method: 'GET', path: '/api/v1/cloud-accounts', desc: 'List all connected accounts' },
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect a new account' },
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect Oracle/OCI account (provider=oracle or oci)' },
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect Azure account (provider=azure)' },
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect IBM Cloud account (provider=ibmcloud)' },
      { method: 'PATCH', path: '/api/v1/cloud-accounts/{id}', desc: 'Update account credentials metadata' },
      { method: 'DELETE', path: '/api/v1/cloud-accounts/{id}', desc: 'Remove an account' },
    ],
    sampleKey: 'list_accounts',
    sampleTitle: 'List connected accounts',
  },
  {
    id: 'aws',
    title: 'Connect AWS',
    badge: 'AWS',
    badgeColor: '#f90',
    description: 'Connect an AWS account via cross-account IAM role. The role must allow sts:AssumeRole from the Sroik service account with the correct external ID.',
    endpoints: [],
    sampleKey: 'add_aws',
    sampleTitle: 'Connect an AWS account',
  },
  {
    id: 'gcp',
    title: 'Connect GCP',
    badge: 'GCP',
    badgeColor: '#34a853',
    description: 'Connect a GCP project by granting the Sroik service account the Browser, Cloud Asset Viewer, and Billing Account Viewer roles.',
    endpoints: [],
    sampleKey: 'add_gcp',
    sampleTitle: 'Connect a GCP project',
  },
  {
    id: 'oci',
    title: 'Connect Oracle/OCI',
    badge: 'OCI',
    badgeColor: '#f56500',
    description: 'Connect an Oracle Cloud tenancy using provider value oracle (or oci alias) and tenancy OCID as account_id.',
    endpoints: [
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect Oracle/OCI tenancy' },
      { method: 'GET', path: '/api/v1/cloud-accounts/bootstrap?provider=oracle', desc: 'Load Oracle/OCI setup metadata' },
    ],
    sampleKey: 'add_oci',
    sampleTitle: 'Connect an Oracle/OCI tenancy',
  },
  {
    id: 'azure',
    title: 'Connect Azure',
    badge: 'AZURE',
    badgeColor: '#0078d4',
    description: 'Connect an Azure subscription using provider value azure and subscription GUID as account_id.',
    endpoints: [
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect Azure subscription' },
      { method: 'GET', path: '/api/v1/cloud-accounts/bootstrap?provider=azure', desc: 'Load Azure setup metadata' },
    ],
    sampleKey: 'add_azure',
    sampleTitle: 'Connect an Azure subscription',
  },
  {
    id: 'ibmcloud',
    title: 'Connect IBM Cloud',
    badge: 'IBM',
    badgeColor: '#0f62fe',
    description: 'Connect IBM Cloud by supplying provider ibmcloud and a valid account identifier/CRN.',
    endpoints: [
      { method: 'POST', path: '/api/v1/cloud-accounts', desc: 'Connect IBM Cloud account' },
      { method: 'GET', path: '/api/v1/cloud-accounts/bootstrap?provider=ibmcloud', desc: 'Load IBM Cloud setup metadata' },
    ],
    sampleKey: 'add_ibmcloud',
    sampleTitle: 'Connect an IBM Cloud account',
  },
  {
    id: 'sync-jobs',
    title: 'Sync Jobs',
    badge: '202',
    badgeColor: '#2ec6b1',
    description: 'Trigger asynchronous recommendation scans and poll job status until completion for enterprise automation workflows.',
    endpoints: [
      { method: 'POST', path: '/api/v1/cloud-accounts/sync-all', desc: 'Start sync for all accessible accounts (returns job_id)' },
      { method: 'POST', path: '/api/v1/cloud-accounts/{id}/sync', desc: 'Start sync for a specific cloud account (returns job_id)' },
      { method: 'GET', path: '/api/v1/cloud-accounts/sync-jobs/{job_id}', desc: 'Get async sync job status and summarized result' },
    ],
    sampleKey: 'sync_job_status',
    sampleTitle: 'Poll async sync job status',
  },
  {
    id: 'recommendations',
    title: 'Recommendations',
    badge: 'GET',
    badgeColor: '#4cc1e4',
    description: 'Retrieve cost and security recommendations generated by Sroik across all connected cloud accounts.',
    endpoints: [
      { method: 'GET', path: '/api/v1/recommendations', desc: 'List all recommendations' },
      { method: 'PATCH', path: '/api/v1/recommendations/{id}/status', desc: 'Update status (new, accepted, implemented, dismissed)' },
      { method: 'PATCH', path: '/api/v1/recommendations/{id}/assign', desc: 'Assign recommendation to a tenant user' },
    ],
    sampleKey: 'recommendations',
    sampleTitle: 'Fetch recommendations',
  },
];

const METHOD_COLOR = {
  GET: '#4cc1e4',
  POST: '#2ec6b1',
  DELETE: '#f06060',
  PATCH: '#f0b040',
  PUT: '#a07ee8',
};

function normalizePath(path) {
  return path
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean);
}

function inferRawBody(schema) {
  if (!schema || schema.type !== 'object' || !schema.properties) {
    return {};
  }
  const out = {};
  Object.entries(schema.properties).forEach(([key, value]) => {
    if (value.type === 'string') out[key] = value.example || '';
    else if (value.type === 'integer' || value.type === 'number') out[key] = value.example || 0;
    else if (value.type === 'boolean') out[key] = value.example || false;
    else if (value.type === 'array') out[key] = [];
    else if (value.type === 'object') out[key] = inferRawBody(value);
    else out[key] = '';
  });
  return out;
}

function buildPostmanCollection(openApi) {
  const paths = openApi?.paths || {};
  const items = [];

  Object.entries(paths).forEach(([path, methods]) => {
    Object.entries(methods || {}).forEach(([method, details]) => {
      const upper = method.toUpperCase();
      if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(upper)) return;

      const contentSchema = details?.requestBody?.content?.['application/json']?.schema;
      const rawBody = inferRawBody(contentSchema);
      const hasBody = ['POST', 'PUT', 'PATCH'].includes(upper);

      items.push({
        name: `${upper} ${path}`,
        request: {
          method: upper,
          header: [
            { key: 'Authorization', value: 'Bearer {{token}}', type: 'text' },
            { key: 'Content-Type', value: 'application/json', type: 'text' },
          ],
          body: hasBody
            ? {
                mode: 'raw',
                raw: JSON.stringify(rawBody, null, 2),
                options: { raw: { language: 'json' } },
              }
            : undefined,
          url: {
            raw: `{{baseUrl}}${path}`,
            host: ['{{baseUrl}}'],
            path: normalizePath(path),
          },
          description: details?.summary || details?.description || '',
        },
      });
    });
  });

  return {
    info: {
      name: 'Sroik API',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      description: 'Generated from OpenAPI at download time.',
    },
    item: items,
    variable: [
      { key: 'baseUrl', value: BASE_URL },
      { key: 'token', value: 'sroik_pat_YOUR_TOKEN' },
    ],
  };
}

function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <div style={{position:'relative', borderRadius:'10px', background:'#05101c', border:'1px solid rgba(76,193,228,0.15)', overflow:'hidden'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.55rem 1rem', background:'rgba(4,16,28,0.8)', borderBottom:'1px solid rgba(76,193,228,0.1)'}}>
        <span style={{fontSize:'0.73rem', fontWeight:700, color: LANG_COLOR[lang] || '#4cc1e4', letterSpacing:'0.06em', textTransform:'uppercase'}}>{LANG_ICON[lang]} {lang}</span>
        <button onClick={copy} style={{background:'none', border:'none', cursor:'pointer', color: copied ? '#2ec6b1' : 'rgba(180,210,230,0.55)', fontSize:'0.72rem', fontWeight:600, padding:'2px 8px'}}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre style={{margin:0, padding:'1.1rem 1.25rem', overflowX:'auto', fontSize:'0.82rem', lineHeight:1.65, color:'#c8e6f5', fontFamily:"'Fira Code', 'Cascadia Code', 'Courier New', monospace", whiteSpace:'pre'}}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function MethodBadge({ method }) {
  return (
    <span style={{display:'inline-block', minWidth:'56px', textAlign:'center', fontSize:'0.68rem', fontWeight:800, letterSpacing:'0.07em', padding:'2px 8px', borderRadius:'4px', color: METHOD_COLOR[method] || '#aaa', background:'rgba(0,0,0,0.35)', border:`1px solid ${METHOD_COLOR[method] || '#aaa'}40`}}>
      {method}
    </span>
  );
}

const API_CHANGELOG = [
  {
    version: 'v2026.04.03',
    date: '2026-04-03',
    changes: [
      'Added async sync job endpoints for account scans and status polling.',
      'Expanded cloud account docs to include Oracle/OCI, Azure, and IBM Cloud onboarding.',
      'Added recommendation assignment endpoint to reference section.',
    ],
  },
  {
    version: 'v2026.03.31',
    date: '2026-03-31',
    changes: [
      'Introduced personal API token lifecycle endpoints (list/create/revoke/delete).',
      'Improved recommendation status workflow documentation and examples.',
    ],
  },
  {
    version: 'v2026.03.25',
    date: '2026-03-25',
    changes: [
      'Published initial enterprise API reference with authenticated endpoint catalog.',
    ],
  },
];

export default function DocsPage() {
  const [lang, setLang] = useState('Bash');
  const [activeSection, setActiveSection] = useState('authentication');
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  async function handleDownloadPostmanCollection() {
    setDownloadBusy(true);
    setDownloadError('');
    try {
      const candidateUrls = [`${BASE_URL}/openapi.json`, '/openapi.json'];
      let openApi = null;
      const failures = [];

      for (const url of candidateUrls) {
        try {
          const response = await fetch(url, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) {
            failures.push(`${url} returned ${response.status}`);
            continue;
          }

          const raw = await response.text();
          if (!raw.trim()) {
            failures.push(`${url} returned an empty response`);
            continue;
          }

          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch {
            const looksLikeHtml = /^\s*</.test(raw);
            failures.push(looksLikeHtml ? `${url} returned HTML instead of JSON` : `${url} returned invalid JSON`);
            continue;
          }

          if (!parsed || typeof parsed !== 'object' || !parsed.paths) {
            failures.push(`${url} returned JSON but not an OpenAPI document`);
            continue;
          }

          openApi = parsed;
          break;
        } catch (error) {
          failures.push(`${url} failed: ${error.message || 'network error'}`);
        }
      }

      if (!openApi) {
        throw new Error(`Unable to download OpenAPI JSON. ${failures.join(' | ')}`);
      }

      const collection = buildPostmanCollection(openApi);
      const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sroik-api.postman_collection.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(error.message || 'Download failed. OpenAPI JSON is unavailable.');
    } finally {
      setDownloadBusy(false);
    }
  }

  const section = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];
  const code = SAMPLES[section.sampleKey]?.[lang] || '';

  return (
    <div style={{minHeight:'100vh', background:'linear-gradient(180deg, #07111e 0%, #040d18 100%)', color:'#e0eff9', fontFamily:"system-ui, -apple-system, sans-serif", display:'flex', flexDirection:'column'}}>

      {/* ── top bar ── */}
      <header style={{borderBottom:'1px solid rgba(76,193,228,0.12)', padding:'0 2rem', height:'60px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, background:'rgba(5,14,25,0.9)', backdropFilter:'blur(8px)', position:'sticky', top:0, zIndex:100}}>
        <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
          <a href="/" style={{color:'#4cc1e4', fontWeight:800, fontSize:'1.1rem', textDecoration:'none', letterSpacing:'-0.02em'}}>sroik</a>
          <span style={{color:'rgba(76,193,228,0.3)', fontSize:'1rem'}}>›</span>
          <span style={{color:'#8ab8d8', fontSize:'0.88rem', fontWeight:500}}>API Reference</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
          <a href={`${BASE_URL}/openapi.json`} target="_blank" rel="noopener noreferrer" style={{color:'rgba(180,210,230,0.55)', fontSize:'0.8rem', textDecoration:'none'}}>OpenAPI JSON</a>
          <button
            type="button"
            onClick={handleDownloadPostmanCollection}
            disabled={downloadBusy}
            style={{fontSize:'0.8rem', fontWeight:600, padding:'6px 12px', borderRadius:'999px', border:'1px solid rgba(76,193,228,0.35)', background:'transparent', color:'rgba(180,210,230,0.85)', cursor:'pointer'}}
          >
            {downloadBusy ? 'Preparing...' : 'Download Postman'}
          </button>
          <a href="/app" style={{fontSize:'0.82rem', fontWeight:600, padding:'6px 18px', borderRadius:'999px', background:'linear-gradient(135deg, #4cc1e4 0%, #1fa58f 100%)', color:'#03131f', textDecoration:'none'}}>Dashboard →</a>
        </div>
      </header>

      <div style={{display:'flex', flex:1, maxWidth:'1280px', width:'100%', margin:'0 auto', padding:'0'}}>

        {/* ── sidebar ── */}
        <nav style={{width:'230px', flexShrink:0, borderRight:'1px solid rgba(76,193,228,0.1)', padding:'2rem 0', position:'sticky', top:'60px', height:'calc(100vh - 60px)', overflowY:'auto'}}>
          <div style={{padding:'0 1.25rem 0.75rem', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(140,180,210,0.45)', textTransform:'uppercase'}}>Endpoints</div>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                display:'block', width:'100%', textAlign:'left', padding:'0.6rem 1.25rem',
                background: activeSection === s.id ? 'rgba(76,193,228,0.1)' : 'none',
                border:'none', borderLeft: activeSection === s.id ? `2px solid ${s.badgeColor}` : '2px solid transparent',
                color: activeSection === s.id ? '#e0eff9' : 'rgba(160,200,225,0.65)',
                cursor:'pointer', fontSize:'0.85rem', fontWeight: activeSection === s.id ? 600 : 400,
                transition:'all 140ms ease',
              }}
            >
              {s.title}
            </button>
          ))}
          <div style={{padding:'1.5rem 1.25rem 0.75rem', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(140,180,210,0.45)', textTransform:'uppercase'}}>Resources</div>
          <a href={`${BASE_URL}/openapi.json`} target="_blank" rel="noopener noreferrer" style={{display:'block', padding:'0.5rem 1.25rem', color:'rgba(160,200,225,0.55)', fontSize:'0.82rem', textDecoration:'none'}}>OpenAPI Spec</a>
          <a href="/app/security" style={{display:'block', padding:'0.5rem 1.25rem', color:'rgba(160,200,225,0.55)', fontSize:'0.82rem', textDecoration:'none'}}>Manage Tokens</a>
        </nav>

        {/* ── main content ── */}
        <main style={{flex:1, padding:'2.5rem 3rem', overflowY:'auto', maxWidth:'900px'}}>

          {/* section header */}
          <div style={{marginBottom:'2rem', paddingBottom:'1.5rem', borderBottom:'1px solid rgba(76,193,228,0.1)'}}>
            <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.6rem'}}>
              <span style={{fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.08em', padding:'3px 10px', borderRadius:'5px', background:`${section.badgeColor}22`, color:section.badgeColor, border:`1px solid ${section.badgeColor}44`}}>
                {section.badge}
              </span>
              <h1 style={{margin:0, fontSize:'1.5rem', fontWeight:700, color:'#e8f4fb'}}>{section.title}</h1>
            </div>
            <p style={{margin:0, color:'#8ab8d8', lineHeight:1.7, fontSize:'0.92rem', maxWidth:'640px'}}>{section.description}</p>
          </div>

          {/* request headers table */}
          {section.headers && section.headers.length > 0 && (
            <div style={{marginBottom:'2rem'}}>
              <h3 style={{fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(140,180,210,0.5)', textTransform:'uppercase', marginBottom:'0.75rem'}}>Request Headers</h3>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.84rem'}}>
                <thead>
                  <tr style={{borderBottom:'1px solid rgba(76,193,228,0.15)'}}>
                    <th style={{textAlign:'left', padding:'0.5rem 0.75rem', color:'rgba(140,180,210,0.6)', fontWeight:600, fontSize:'0.78rem', width:'220px'}}>Header</th>
                    <th style={{textAlign:'left', padding:'0.5rem 0.75rem', color:'rgba(140,180,210,0.6)', fontWeight:600, fontSize:'0.78rem', width:'80px'}}>Required</th>
                    <th style={{textAlign:'left', padding:'0.5rem 0.75rem', color:'rgba(140,180,210,0.6)', fontWeight:600, fontSize:'0.78rem'}}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {section.headers.map(h => (
                    <tr key={h.name} style={{borderBottom:'1px solid rgba(76,193,228,0.07)'}}>
                      <td style={{padding:'0.6rem 0.75rem'}}><code style={{background:'rgba(76,193,228,0.1)', color:'#4cc1e4', padding:'2px 7px', borderRadius:'4px', fontSize:'0.81rem'}}>{h.name}</code></td>
                      <td style={{padding:'0.6rem 0.75rem', color: h.required ? '#2ec6b1' : 'rgba(140,180,210,0.4)', fontSize:'0.78rem', fontWeight:600}}>{h.required ? 'Yes' : 'No'}</td>
                      <td style={{padding:'0.6rem 0.75rem', color:'rgba(180,210,230,0.7)', fontSize:'0.83rem'}}>{h.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* endpoint list */}
          {section.endpoints && section.endpoints.length > 0 && (
            <div style={{marginBottom:'2rem'}}>
              <h3 style={{fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(140,180,210,0.5)', textTransform:'uppercase', marginBottom:'0.75rem'}}>Endpoints</h3>
              <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
                {section.endpoints.map(e => (
                  <div key={e.path} style={{display:'flex', alignItems:'center', gap:'1rem', padding:'0.65rem 1rem', background:'rgba(5,16,30,0.6)', border:'1px solid rgba(76,193,228,0.12)', borderRadius:'8px'}}>
                    <MethodBadge method={e.method} />
                    <code style={{color:'#c8e6f5', fontSize:'0.84rem', fontFamily:"'Fira Code', monospace", flex:1}}>{e.path}</code>
                    <span style={{color:'rgba(140,180,210,0.55)', fontSize:'0.8rem'}}>{e.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* code sample */}
          {code && (
            <div>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.9rem', flexWrap:'wrap', gap:'0.75rem'}}>
                <h3 style={{fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.1em', color:'rgba(140,180,210,0.5)', textTransform:'uppercase', margin:0}}>{section.sampleTitle}</h3>
                {/* language switcher */}
                <div style={{display:'flex', gap:'4px', background:'rgba(4,12,22,0.8)', borderRadius:'8px', padding:'3px', border:'1px solid rgba(76,193,228,0.13)'}}>
                  {LANGS.map(l => (
                    <button
                      key={l}
                      onClick={() => setLang(l)}
                      style={{
                        padding:'4px 14px', borderRadius:'5px', border:'none', cursor:'pointer',
                        fontSize:'0.78rem', fontWeight:600,
                        background: lang === l ? `${LANG_COLOR[l]}22` : 'transparent',
                        color: lang === l ? LANG_COLOR[l] : 'rgba(160,200,225,0.45)',
                        transition:'all 140ms',
                      }}
                    >
                      {LANG_ICON[l]} {l}
                    </button>
                  ))}
                </div>
              </div>
              <CodeBlock code={code} lang={lang} />
            </div>
          )}

          {/* base URL callout */}
          <div style={{marginTop:'3rem', padding:'1.1rem 1.4rem', background:'rgba(76,193,228,0.05)', border:'1px solid rgba(76,193,228,0.18)', borderRadius:'10px', display:'flex', alignItems:'flex-start', gap:'0.9rem'}}>
            <span style={{fontSize:'1rem', marginTop:'1px'}}>ℹ️</span>
            <div>
              <div style={{fontSize:'0.8rem', fontWeight:600, color:'#4cc1e4', marginBottom:'0.3rem'}}>Base URL</div>
              <code style={{fontSize:'0.85rem', color:'#c8e6f5', fontFamily:"'Fira Code', monospace"}}>{BASE_URL}</code>
              <div style={{marginTop:'0.5rem', fontSize:'0.78rem', color:'rgba(140,180,210,0.65)'}}>All requests must be made over HTTPS. Replace <code style={{color:'#4cc1e4'}}>sroik_pat_YOUR_TOKEN</code> with a real token from the <a href="/app/security" style={{color:'#4cc1e4'}}>Security page</a>.</div>
            </div>
          </div>

          <div style={{marginTop:'1rem', padding:'1.1rem 1.4rem', background:'rgba(46,198,177,0.05)', border:'1px solid rgba(46,198,177,0.2)', borderRadius:'10px'}}>
            <div style={{fontSize:'0.8rem', fontWeight:600, color:'#2ec6b1', marginBottom:'0.5rem'}}>Enterprise Integration Notes</div>
            <div style={{fontSize:'0.8rem', color:'rgba(180,210,230,0.78)', lineHeight:1.6}}>
              Use async sync endpoints for large account fleets, store only token hashes or encrypted secrets in your own systems, and implement retries with exponential backoff for 429/5xx responses.
            </div>
          </div>

          {downloadError && (
            <p style={{marginTop:'0.75rem', color:'#f49a9a', fontSize:'0.8rem'}}>{downloadError}</p>
          )}

          <div style={{marginTop:'1rem', padding:'1.1rem 1.4rem', background:'rgba(76,193,228,0.05)', border:'1px solid rgba(76,193,228,0.18)', borderRadius:'10px'}}>
            <div style={{fontSize:'0.8rem', fontWeight:600, color:'#4cc1e4', marginBottom:'0.7rem'}}>API Changelog</div>
            <div style={{display:'flex', flexDirection:'column', gap:'0.85rem'}}>
              {API_CHANGELOG.map((entry) => (
                <div key={entry.version} style={{border:'1px solid rgba(76,193,228,0.12)', borderRadius:'8px', padding:'0.8rem 0.9rem', background:'rgba(5,16,30,0.45)'}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem'}}>
                    <strong style={{fontSize:'0.84rem', color:'#d9eefc'}}>{entry.version}</strong>
                    <span style={{fontSize:'0.75rem', color:'rgba(160,200,225,0.7)'}}>{entry.date}</span>
                  </div>
                  <ul style={{margin:'0', paddingLeft:'1.1rem', color:'rgba(180,210,230,0.82)', fontSize:'0.79rem', lineHeight:1.5}}>
                    {entry.changes.map((change) => (
                      <li key={change}>{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}
